import { useState } from 'react';
import { useNavigate } from 'react-router';

import { Box, List, ListItem, ListItemText, Collapse } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockOpenTwoToneIcon from '@mui/icons-material/LockOpenTwoTone';

const QUICK_START_GUIDE_URL = process.env.REACT_APP_QUICK_START_GUIDE_URL;
const PERIO_GUIDE_URL = process.env.REACT_APP_PERIO_GUIDE_URL;

interface SubMenuOption {
  name: string;
  link?: string;
  onClick?: (link?: any) => void;
  disabled?: boolean;
}

interface MenuOption {
  name: string;
  subMenuArr?: SubMenuOption[];
  link?: string;
  comp?: JSX.Element;
}

const getAccountSubscriptionMenuOptions = (
  cancelSubscription?: SubMenuOption
): MenuOption[] => [
  {
    name: 'Account',
    subMenuArr: [
      {
        name: 'Manage Profile',
        link: '/accounts/profile'
      }
    ]
  },
  {
    name: 'Subscription Management',
    subMenuArr: [
      {
        name: 'Manage Subscription',
        link: '/subscriptions/plan'
      },
      ...(cancelSubscription ? [cancelSubscription] : [])
    ]
  }
];

const ProfileMenu = ({
  showCancelSubscriptionOption,
  showReportsOption,
  handleCancelSubscriptionModalOpen,
  cancelSubscriptionBtnDisabled,
  handleLogout,
  showUserProfileMenuOptions,
  showAccountSubscriptionMenuOptions
}): MenuOption[] => {
  const cancelSubscriptionOption: SubMenuOption | undefined =
    showCancelSubscriptionOption
      ? {
          name: 'Cancel Subscription',
          onClick: () => handleCancelSubscriptionModalOpen(),
          disabled: cancelSubscriptionBtnDisabled
        }
      : undefined;
  const accountSubscriptionOptions = getAccountSubscriptionMenuOptions(
    cancelSubscriptionOption
  );

  let menuOptions: MenuOption[] = [];
  if (showUserProfileMenuOptions) {
    menuOptions = [
      {
        name: 'My Profile',
        subMenuArr: [
          {
            name: 'View/Update Profile',
            link: '/users/update-profile'
          },
          {
            name: 'View/Update Practice',
            link: '/users/update-practice'
          },
          {
            name: 'Security',
            link: '/users/change-password'
          }
        ]
      },
      ...accountSubscriptionOptions,
      ...(showReportsOption
        ? [
            {
              name: 'Reports',
              link: '/reports/all'
            }
          ]
        : []),
      {
        name: 'Billing and Payment',
        subMenuArr: [
          {
            name: 'Card Details',
            link: '/subscriptions/payment'
          },
          {
            name: 'Invoices',
            link: '/users/billing-invoices'
          }
        ]
      },
      // TODO: for 2nd phase DEN-390
      {
        name: 'Account Settings',
        subMenuArr: [
          {
            name: 'Notifications',
            link: '/account-settings/notification-preferences'
          },
          {
            name: 'Two-Factor Authentication',
            link: '/account-settings/mfa-setting'
          }
          // { name: 'Account deletion or deactivation' }
        ]
      },
      {
        name: 'Schedule a Service',
        subMenuArr: [
          {
            name: 'Training',
            link: '/training'
          },
          {
            name: 'Support',
            link: '/support'
          },
          {
            name: 'Installation',
            link: '/installation'
          }
        ]
      },
      {
        name: 'Resources',
        subMenuArr: [
          {
            name: 'Learning',
            link: '/resources'
          },
          {
            name: 'Quick Start Guide',
            link: QUICK_START_GUIDE_URL
          },
          {
            name: 'Perio Charting Guide',
            link: PERIO_GUIDE_URL
          },
          {
            name: 'FAQs',
            link: '/faq'
          }
        ]
      }
    ];
  } else if (showAccountSubscriptionMenuOptions) {
    // Trial: only moved sidebar items (Account + Subscriptions)
    menuOptions = accountSubscriptionOptions;
  }
  menuOptions = [
    ...menuOptions,
    {
      name: 'Logout',
      comp: (
        <Box display={'flex'} alignItems={'center'} onClick={handleLogout}>
          <LockOpenTwoToneIcon sx={{ mr: 1 }} color="primary" /> Logout
        </Box>
      )
    }
  ];

  return menuOptions;
};

interface ProfileMenuProps {
  showCancelSubscriptionOption: boolean;
  showReportsOption: boolean;
  handleCancelSubscriptionModalOpen: () => void;
  cancelSubscriptionBtnDisabled: boolean;
  handleCloseMenu: () => void;
  handleLogout: () => void;
  showUserProfileMenuOptions: boolean;
  showAccountSubscriptionMenuOptions: boolean;
}

const ProfileMenuComponent: React.FC<ProfileMenuProps> = ({
  showCancelSubscriptionOption,
  showReportsOption,
  handleCancelSubscriptionModalOpen,
  cancelSubscriptionBtnDisabled,
  handleCloseMenu,
  handleLogout,
  showUserProfileMenuOptions,
  showAccountSubscriptionMenuOptions
}) => {
  const [openSubMenu, setOpenSubMenu] = useState<string | null>(null);
  const navigate = useNavigate();

  const handleToggle = (menuName: string | null) => {
    setOpenSubMenu((prev) => (prev === menuName ? null : menuName));
  };

  const handleOptionClickNavigation = (link?: string) => {
    if (link) {
      // // to close the menu
      handleCloseMenu();

      // // to reset selected menu option
      handleToggle(null);

      if (link?.startsWith('http') || link?.toLowerCase()?.endsWith('.pdf')) {
        window.open(link, '_blank', 'noopener noreferrer');
      } else {
        navigate(link);
      }
    }
  };

  const handleMainMenuOptionClick = (menu: MenuOption) => {
    // // set the current main menu option name
    handleToggle(menu.name);

    if (menu.link) {
      // // handle navigation on main menu option click
      handleOptionClickNavigation(menu.link);
    }
  };

  return (
    <List>
      {ProfileMenu({
        showCancelSubscriptionOption,
        showReportsOption,
        handleCancelSubscriptionModalOpen,
        cancelSubscriptionBtnDisabled,
        handleLogout,
        showUserProfileMenuOptions,
        showAccountSubscriptionMenuOptions
      }).map((menu, menuIndex) => (
        <Box key={`${menu.name}-${menuIndex}`}>
          <ListItem button onClick={() => handleMainMenuOptionClick(menu)}>
            <ListItemText primary={menu.comp || menu.name} />
            {menu.subMenuArr?.length > 0 ? (
              openSubMenu === menu.name ? (
                <ExpandLessIcon />
              ) : (
                <ExpandMoreIcon />
              )
            ) : null}
          </ListItem>
          <Collapse in={openSubMenu === menu.name} timeout="auto" unmountOnExit>
            <List component="div" disablePadding>
              {menu?.subMenuArr?.map((subMenu, subIndex) => (
                <ListItem
                  key={`${subMenu}-${subIndex}`}
                  button
                  component={subMenu?.link ? 'a' : 'button'}
                  sx={{ pl: 4 }}
                  disabled={subMenu?.disabled ? subMenu.disabled : undefined}
                  onClick={
                    subMenu?.onClick
                      ? subMenu?.onClick
                      : () => handleOptionClickNavigation(subMenu.link)
                  }
                >
                  <ListItemText primary={subMenu?.name} />
                </ListItem>
              ))}
            </List>
          </Collapse>
        </Box>
      ))}
    </List>
  );
};

export default ProfileMenuComponent;
